import ssrPrepass from 'react-ssr-prepass'
import {dehydrate} from '@tanstack/react-query'
import {withReactQuery} from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-react-query'
import {isBundleSet} from './bundle-utils'

const withBundleEditorialSSR = (Component, options) => {
    const QueryApp = withReactQuery(Component, options)

    return class BundleQueryApp extends QueryApp {
        static async doInitAppState(args) {
            // Keep the SDK's original query collection and fetching as the first step.
            // Product type is unknown until this completes, so do not guess from the URL.
            const state = await QueryApp.doInitAppState(args)
            const queryClient = args.res.locals.__queryClient
            const queryCache = queryClient.getQueryCache()
            const hasBundlePdp = queryCache
                .getAll()
                .some((query) => query.meta?.bundleEditorialPdp && isBundleSet(query.state.data))

            // Ordinary PDPs keep the SDK's original single-prepass initialization.
            if (!hasBundlePdp) return state

            // The SDK uses one prepass, which misses queries enabled by later responses:
            // bundle -> full primary product -> editorial components and their queries.
            // Repeat query discovery only for confirmed bundle PDPs to resolve that chain.
            // Fetch only new, enabled queries; never refetch successful or failed queries.
            // Stop when nothing remains to fetch; the cap bounds unexpectedly deep chains.
            for (let pass = 0; pass < 5; pass++) {
                // Each pass depends on the queries discovered and fetched in the previous pass.
                // eslint-disable-next-line no-await-in-loop
                await ssrPrepass(args.appJSX)
                const pendingQueries = queryCache
                    .getAll()
                    .filter(
                        (query) =>
                            query.options.enabled !== false && query.state.status === 'loading',
                    )
                if (!pendingQueries.length) break
                // eslint-disable-next-line no-await-in-loop
                await Promise.allSettled(pendingQueries.map((query) => query.fetch()))
            }

            // Include the additional editorial data in the browser's hydration state too.
            return {...state, __reactQuery: dehydrate(queryClient)}
        }

        static getInitializers() {
            // Replace only the SDK's React Query initializer, preserving any other ones.
            const [, ...otherInitializers] = QueryApp.getInitializers()
            return [this.doInitAppState, ...otherInitializers]
        }
    }
}

export default withBundleEditorialSSR
